/* ==========================================================================
   Suki x Invisible Building 2.0 - theme-collection.js

   Collection and search runtime: the facets module (filter, sort and
   pagination without a page load) and the predictiveSearch module (header
   search dialog). Loaded with `defer` by layout/theme.liquid on the
   collection, search and list-collections templates, and lazily by the
   header module in theme.js (Suki.loadScript) the first time the search
   dialog opens. Gate: 24 KB gzip (decision A4). Vanilla, no build step.

   Idempotent: a second copy of this file finds window.__sukiCollectionRuntime
   and returns; window.Suki (theme.js, always earlier in document order)
   is required.

   Hook contract, Phase 7
   ------------------------------------------------------------------------
   Facets     [data-cpage] root of a collection or search grid section.
              form[data-cfilters] a real GET form to the results URL (hidden
              sort_by, hidden q on search, <noscript> submit);
              [data-cfilters-clear] the remove-all link;
              button[data-cgrid-add="flavour|moment"] the Add a flavour /
              Add a feeling prompts in the pills row (open that group);
              select[data-cpage-sort] the sort control (its own GET form);
              [data-cpage-count] live region ("x of y items");
              #collection-grid-region[data-cgrid-region] the single swap
              target holding [data-cgrid-pill] links (url_to_remove),
              the product grid and pagination links [data-cgrid-page];
              dialog[data-cfilters-dialog] the mobile filter panel, opened by
              the core dialogs module and closed here when the viewport
              reaches 990px.
              Behaviour: change and submit on the filter form, change on the
              sort select and clicks on pills, pagination and clear links all
              build the real, shareable URL from the form (never a
              section_id), debounce 220 ms, fetch with X-Requested-With,
              parse with DOMParser, replace the region node, refresh the
              filter form (counts and disabled states, open groups and focus
              kept), the count and the sort selection, pushState, re-render
              on popstate, aria-busy while loading, and hard-navigate to the
              same URL on any failure. Suki.init runs on the fresh region so
              reveal and quick add pick it up.
   Search     [data-search-input] (input[name=q]) inside a container that
              also holds [data-predictive-results]; the container is
              [data-search-dialog] or the input's form. Debounced 250 ms
              fetch of config.routes.predictive_search with
              section_id=predictive-search, previous request aborted,
              the static section's markup injected into the results region,
              combobox semantics on the input; the section's own
              [role="listbox"] and [role="option"] markup is navigated when
              present, else the container becomes the listbox and each link
              an option; ArrowUp/Down,
              Home/End, Enter and Escape handled on the input. Under two
              characters clears the results.
              Also: [data-cpage-sync] elements (the dialog's foot, with
              "Show n results") are refreshed from the response; every group
              opens with the filter dialog below 990px; in the bar a ticked
              chip closes its dropdown; the group foot's
              [data-cfilters-group-clear] unticks its group and
              [data-cfilters-done] closes it; [data-loading] on the root while
              a fetch runs; .cpage__bar sticks under .site-chrome, with
              --cpage-sticky-top kept at the chrome's bottom edge and
              .is-stuck set once it has stuck.
   Deck       [data-cspot-deck]: a scroll-snap [data-cspot-track] of
              [data-cspot-card] items; [data-cspot-prev] / [data-cspot-next]
              buttons, [data-cspot-go="i"] progress segments and a
              [data-cspot-current] counter follow the snapped card, which
              gets data-active. Mouse drag scrolls the row; a click on a
              card not yet snapped in brings it in instead of opening it.
   Events     suki:facets:updated { url, region }, suki:search:results
              { query, results }
   ========================================================================== */

(function () {
  'use strict';

  if (window.__sukiCollectionRuntime) return;
  window.__sukiCollectionRuntime = true;

  var Suki = window.Suki;
  if (!Suki || typeof Suki.register !== 'function') return;

  var doc = document;
  var each = function (list, fn) { Array.prototype.forEach.call(list || [], fn); };
  var desktop = window.matchMedia ? window.matchMedia('(min-width: 990px)') : null;

  function onMedia(query, fn) {
    if (!query) return;
    if (query.addEventListener) query.addEventListener('change', fn);
    else if (query.addListener) query.addListener(fn);
  }

  function parseHtml(html) {
    return new DOMParser().parseFromString(String(html || ''), 'text/html');
  }

  function stripHash(href) {
    return String(href || '').split('#')[0];
  }

  /* ------------------------------------------------------------------------
     Module: facets
     ------------------------------------------------------------------------ */

  Suki.register('facets', function init(scope) {
    if (!scope.querySelectorAll) return;
    var roots = [];
    if (scope !== doc && scope.matches && scope.matches('[data-cpage]')) roots.push(scope);
    Array.prototype.push.apply(roots, scope.querySelectorAll('[data-cpage]'));
    roots.forEach(bindFacets);
  });

  function bindFacets(root) {
    if (!root.querySelector('[data-cgrid-region]') || !Suki.claim(root, 'facets')) return;

    var controller = null;
    var lastUrl = stripHash(window.location.href);
    var timer = null;

    function filterForm() { return root.querySelector('[data-cfilters]'); }
    function sortSelect() { return root.querySelector('[data-cpage-sort]'); }
    function region() { return root.querySelector('[data-cgrid-region]'); }
    function dialog() { return root.querySelector('[data-cfilters-dialog]'); }

    /* The shareable URL: the filter form's action and fields, plus the sort. */
    function buildUrl() {
      var form = filterForm();
      var select = sortSelect();
      var base = (form && form.getAttribute('action')) || (select && select.form && select.form.getAttribute('action')) || window.location.pathname;
      var url = new URL(base, window.location.origin);
      url.hash = '';
      url.search = '';

      if (form) {
        new FormData(form).forEach(function (value, key) {
          if (value === '' || key === 'sort_by') return;
          url.searchParams.append(key, value);
        });
      } else if (select && select.form) {
        new FormData(select.form).forEach(function (value, key) {
          if (value === '' || key === 'sort_by') return;
          url.searchParams.append(key, value);
        });
      }

      var sort = select && select.value;
      if (!sort && form) {
        var hidden = form.querySelector('input[name="sort_by"]');
        sort = hidden && hidden.value;
      }
      if (sort) url.searchParams.set('sort_by', sort);
      return url;
    }

    function setBusy(busy) {
      var current = region();
      if (current) current.setAttribute('aria-busy', busy ? 'true' : 'false');
      root.toggleAttribute('data-loading', Boolean(busy));
      var select = sortSelect();
      if (select) select.disabled = Boolean(busy);
    }

    /* Filter form refresh keeps whatever the shopper had open or focused. */
    function refreshFilters(fresh) {
      var form = filterForm();
      if (!form || !fresh) return;
      var open = [];
      each(form.querySelectorAll('details'), function (d, i) { if (d.open) open.push(i); });
      var active = doc.activeElement;
      var focusId = active && form.contains(active) ? active.id : '';

      form.innerHTML = fresh.innerHTML;
      var groups = form.querySelectorAll('details');
      /* Exactly the groups the shopper had open stay open (the server opens
         every group with an active value); data-restored skips the drop-in
         animation on the ones reopened here. */
      each(groups, function (d, i) {
        var keep = open.indexOf(i) !== -1;
        if (keep) d.setAttribute('data-restored', '');
        d.open = keep;
      });
      if (focusId) {
        var again = doc.getElementById(focusId);
        if (again) again.focus({ preventScroll: true });
      }
    }

    function scrollToRegion(force) {
      var current = region();
      if (!current) return;
      var headerHeight = parseFloat(getComputedStyle(doc.documentElement).getPropertyValue('--header-height')) || 0;
      var top = current.getBoundingClientRect().top + window.scrollY - headerHeight - 24;
      if (force || window.scrollY > top) {
        window.scrollTo({ top: Math.max(0, top), behavior: Suki.prefersReducedMotion() ? 'auto' : 'smooth' });
      }
    }

    function load(url, opts) {
      var options = opts || {};
      var href = stripHash(url.toString());
      if (controller) controller.abort();
      controller = 'AbortController' in window ? new AbortController() : null;
      setBusy(true);

      return fetch(href, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: controller ? controller.signal : undefined
      }).then(function (response) {
        if (!response.ok) throw new Error(response.statusText);
        return response.text();
      }).then(function (html) {
        var fresh = parseHtml(html);
        var freshRegion = fresh.querySelector('[data-cgrid-region]');
        var current = region();
        if (!freshRegion || !current) throw new Error('region missing');

        current.replaceWith(freshRegion);
        refreshFilters(fresh.querySelector('[data-cfilters]'));

        var freshCount = fresh.querySelector('[data-cpage-count]');
        var count = root.querySelector('[data-cpage-count]');
        if (freshCount && count) count.innerHTML = freshCount.innerHTML;

        var freshSummary = fresh.querySelector('[data-cpage-summary]');
        var summary = doc.querySelector('[data-cpage-summary]');
        if (freshSummary && summary) summary.innerHTML = freshSummary.innerHTML;

        var freshSync = fresh.querySelectorAll('[data-cpage-sync]');
        each(root.querySelectorAll('[data-cpage-sync]'), function (el, i) {
          if (freshSync[i]) el.innerHTML = freshSync[i].innerHTML;
        });

        var freshSort = fresh.querySelector('[data-cpage-sort]');
        var select = sortSelect();
        if (freshSort && select && select.value !== freshSort.value) select.value = freshSort.value;

        if (options.push !== false) {
          window.history.pushState({ sukiFacets: true }, '', href);
        }
        lastUrl = href;
        setBusy(false);
        controller = null;

        Suki.init(freshRegion, 'facets:swap');
        scrollToRegion(Boolean(options.scroll));
        Suki.emit('suki:facets:updated', { url: href, region: freshRegion }, root);
      }).catch(function (err) {
        if (err && err.name === 'AbortError') return;
        setBusy(false);
        window.location.assign(href);
      });
    }

    function refresh() {
      clearTimeout(timer);
      timer = setTimeout(function () { load(buildUrl()); }, 220);
    }

    root.addEventListener('change', function (event) {
      var target = event.target;
      if (target.closest('[data-cpage-sort]')) {
        clearTimeout(timer);
        load(buildUrl());
        return;
      }
      var form = target.closest('[data-cfilters]');
      if (form && target.matches('input, select')) refresh();

      /* In the bar a pick closes its dropdown; the button then names it */
      if (form && target.type === 'checkbox' && barGroups().length) {
        var group = target.closest('details');
        /* The merged dropdown (one for every group) stays open for more picks */
        if (group && group.open && !group.classList.contains('cfilters__group--merged')) {
          group.open = false;
          group.querySelector('summary').focus({ preventScroll: true });
        }
      }
    });

    root.addEventListener('submit', function (event) {
      var form = event.target;
      if (!form.matches('[data-cfilters], [data-cpage-sort-form]')) return;
      event.preventDefault();
      clearTimeout(timer);
      load(buildUrl());
    });

    /* Group foot: Clear unticks that group; both it and Show n results
       close the group. The filter dialog below 990px opens with every
       group expanded. */
    root.addEventListener('click', function (event) {
      if (event.target.closest('[data-dialog-open]') && !(desktop && desktop.matches)) {
        var form = filterForm();
        if (form) each(form.querySelectorAll('details'), function (d) { d.open = true; });
        return;
      }
      var clear = event.target.closest('[data-cfilters-group-clear]');
      var done = event.target.closest('[data-cfilters-done]');
      var group = (clear || done) && (clear || done).closest('details');
      if (!group) return;
      if (clear) {
        each(group.querySelectorAll('input'), function (input) {
          if (input.type === 'checkbox') input.checked = false;
          else input.value = '';
        });
        refresh();
      }
      group.open = false;
      group.querySelector('summary').focus();
    });

    root.addEventListener('click', function (event) {
      var link = event.target.closest('[data-cgrid-pill], [data-cgrid-page], [data-cfilters-clear]');
      if (!link || !link.href) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
      event.preventDefault();
      clearTimeout(timer);
      load(new URL(link.href, window.location.origin), { scroll: link.hasAttribute('data-cgrid-page') });
    });

    /* Add a flavour / Add a feeling (pills row): open that filter group. In
       the bar it drops down with focus on the first free chip; below 990px
       the filter dialog opens and scrolls to it. */
    root.addEventListener('click', function (event) {
      var add = event.target.closest('[data-cgrid-add]');
      if (!add) return;
      var form = filterForm();
      var group = form && form.querySelector('.cfilters__group--' + add.getAttribute('data-cgrid-add'));
      if (!group) return;
      var mobile = Boolean(desktop && !desktop.matches);
      if (mobile) {
        var toggle = root.querySelector('[data-dialog-open]');
        if (toggle) toggle.click();
      }
      group.open = true;
      var chip = group.querySelector('.cfilters__chip-input:not(:disabled):not(:checked)') || group.querySelector('summary');
      if (mobile) group.scrollIntoView({ block: 'start' });
      if (chip) chip.focus({ preventScroll: mobile });
    });

    window.addEventListener('popstate', function () {
      var href = stripHash(window.location.href);
      if (href === lastUrl || !doc.contains(root)) return;
      load(new URL(href), { push: false });
    });

    /* Bar filters (.cfilters--bar, from 990px): the groups are dropdowns, so
       one open at a time, and a click outside or Escape closes them. */
    function barGroups() {
      var form = filterForm();
      if (!form || !form.classList.contains('cfilters--bar') || (desktop && !desktop.matches)) return [];
      return form.querySelectorAll('details[open]');
    }

    /* A shared link opens the active groups server side; as dropdowns they
       would cover the grid on arrival, so start them closed. */
    each(barGroups(), function (d) { d.open = false; });

    root.addEventListener('toggle', function (event) {
      var opened = event.target;
      if (opened.matches && opened.matches('details') && !opened.open) opened.removeAttribute('data-restored');
      if (!opened.open || !opened.matches || !opened.matches('details')) return;
      each(barGroups(), function (d) { if (d !== opened) d.open = false; });
    }, true);

    doc.addEventListener('click', function (event) {
      if (event.target.closest && event.target.closest('[data-cgrid-add]')) return;
      each(barGroups(), function (d) { if (!d.contains(event.target)) d.open = false; });
    });

    root.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      each(barGroups(), function (d) {
        var hadFocus = d.contains(doc.activeElement);
        d.open = false;
        if (hadFocus) d.querySelector('summary').focus();
      });
    });

    /* Sticky bar: follow the chrome's bottom edge, which moves as the nav
       hides on scroll down and the top bar scrolls away. */
    var bar = root.querySelector('.cpage__bar');
    var chrome = doc.querySelector('.site-chrome');
    var frame = 0;

    function measureSticky() {
      frame = 0;
      if (!bar || !doc.contains(root)) return;
      var top = chrome ? Math.max(0, chrome.getBoundingClientRect().bottom) : 0;
      root.style.setProperty('--cpage-sticky-top', top + 'px');
      bar.classList.toggle('is-stuck', window.scrollY > 0 && bar.getBoundingClientRect().top <= top + 1);
    }

    function queueSticky() {
      if (!frame) frame = window.requestAnimationFrame(measureSticky);
    }

    if (bar) {
      window.addEventListener('scroll', queueSticky, { passive: true });
      window.addEventListener('resize', queueSticky);
      measureSticky();
    }

    /* From 990px, "Filters" in the bar ([data-cfilters-all]) opens the
       filter panel as a drawer with every group expanded: the form drops
       .cfilters--bar while it is open (so it stacks as in the mobile panel)
       and gets it back, groups closed, when the drawer closes. */
    root.addEventListener('click', function (event) {
      var all = event.target.closest('[data-cfilters-all]');
      var panel = dialog();
      var form = filterForm();
      if (!all || !panel || !form || panel.open || (desktop && !desktop.matches)) return;
      form.classList.remove('cfilters--bar');
      panel.classList.add('is-drawer');
      each(form.querySelectorAll('details'), function (d) { d.open = true; });
      Suki.openDialog(panel, all);
      if (Suki.claim(panel, 'drawer')) {
        panel.addEventListener('close', function () {
          if (!panel.classList.contains('is-drawer')) return;
          panel.classList.remove('is-drawer');
          var f = filterForm();
          if (!f) return;
          f.classList.add('cfilters--bar');
          each(f.querySelectorAll('details'), function (d) { d.open = false; });
        });
      }
    });

    /* The mobile filter panel is inline in the bar from 990px: close it
       (unless it is the desktop drawer). */
    onMedia(desktop, function (event) {
      var panel = dialog();
      if (event.matches && panel && panel.open && !panel.classList.contains('is-drawer')) Suki.closeDialog(panel);
    });
  }

  /* ------------------------------------------------------------------------
     Module: spotlightDeck
     [data-cspot-deck] holds a scroll-snap [data-cspot-track] of
     [data-cspot-card] items (snippets/collection-spotlight). Touch and
     trackpads scroll the row natively; this keeps the foot in step with the
     snapped card (data-active on it, aria-current on its segment, the
     counter, prev / next disabled at the ends, the live region), adds mouse
     drag (snap off while dragging, then a smooth scroll to the card the
     drag points at), arrow keys, and turns a click on a dimmed, half-scrolled
     card into a scroll to it.
     ------------------------------------------------------------------------ */

  /* [data-cpage-spot-close] hides the spotlight ([data-cpage-spot]) for the
     rest of the session: html.spot-closed, which the section's inline
     script also sets before paint on the next page. The bar's
     [data-cpage-spot-open] ("View featured", shown only while closed)
     pops it back in place (.is-returning), without scrolling.

     Choosing a filter on the page tucks the spotlight away too (only where
     View featured is there to bring it back), for this page only: nothing
     is stored, so arriving on a filtered URL (Flavours & Feelings) still
     shows it. Clearing every filter brings it back unless the shopper
     closed it themselves. */
  doc.addEventListener('suki:facets:updated', function (event) {
    if (!doc.querySelector('[data-cpage-spot-open]')) return;
    var filtered = false;
    try {
      new URL(event.detail.url, window.location.origin).searchParams.forEach(function (value, key) {
        if (key.indexOf('filter.') === 0) filtered = true;
      });
    } catch (e) { return; }
    var closedByShopper = false;
    try { closedByShopper = Boolean(sessionStorage.getItem('suki:spot-closed')); } catch (e) { /* storage is best effort */ }
    doc.documentElement.classList.toggle('spot-closed', filtered || closedByShopper);
  });

  Suki.register('spotlightClose', function init(scope) {
    if (!scope.querySelectorAll) return;
    each(scope.querySelectorAll('[data-cpage-spot-close]'), function (button) {
      if (!Suki.claim(button, 'spot-close')) return;
      button.addEventListener('click', function () {
        try { sessionStorage.setItem('suki:spot-closed', '1'); } catch (e) { /* storage is best effort */ }
        doc.documentElement.classList.add('spot-closed');
        var open = doc.querySelector('[data-cpage-spot-open]');
        if (open) open.focus({ preventScroll: true });
      });
    });
    each(scope.querySelectorAll('[data-cpage-spot-open]'), function (button) {
      if (!Suki.claim(button, 'spot-open')) return;
      button.addEventListener('click', function () {
        try { sessionStorage.removeItem('suki:spot-closed'); } catch (e) { /* storage is best effort */ }
        doc.documentElement.classList.remove('spot-closed');
        /* It pops back where it was; the page is not scrolled to it */
        var spot = doc.querySelector('[data-cpage-spot]');
        if (!spot) return;
        spot.classList.remove('is-returning');
        void spot.offsetWidth;
        spot.classList.add('is-returning');
        spot.addEventListener('animationend', function () { spot.classList.remove('is-returning'); }, { once: true });
        var close = spot.querySelector('[data-cpage-spot-close]');
        if (close) close.focus({ preventScroll: true });
      });
    });
  });

  Suki.register('spotlightDeck', function init(scope) {
    if (!scope.querySelectorAll) return;
    each(scope.querySelectorAll('[data-cspot-deck]'), bindDeck);
  });

  function bindDeck(deck) {
    var track = deck.querySelector('[data-cspot-track]');
    var cards = Array.prototype.slice.call(deck.querySelectorAll('[data-cspot-card]'));
    if (!track || cards.length < 2 || !Suki.claim(deck, 'deck')) return;

    var segs = deck.querySelectorAll('[data-cspot-go]');
    var prev = deck.querySelector('[data-cspot-prev]');
    var next = deck.querySelector('[data-cspot-next]');
    var current = deck.querySelector('[data-cspot-current]');
    var status = deck.querySelector('[data-cspot-status]');
    var template = deck.getAttribute('data-status-template') || '';
    var last = cards.length - 1;
    var index = -1;
    var frame = 0;
    var drag = null;
    var suppressClick = false;

    function offsetOf(i) { return cards[i].offsetLeft - cards[0].offsetLeft; }

    /* The card nearest the scroll position; at the far end, the last one
       (it can never reach the start edge itself) */
    function nearest() {
      var x = track.scrollLeft;
      if (x >= track.scrollWidth - track.clientWidth - 2) return last;
      var best = 0;
      for (var i = 1; i <= last; i++) {
        if (Math.abs(offsetOf(i) - x) < Math.abs(offsetOf(best) - x)) best = i;
      }
      return best;
    }

    function setActive(i, announce) {
      if (i === index) return;
      index = i;
      cards.forEach(function (card, j) { card.toggleAttribute('data-active', j === i); });
      each(segs, function (seg, j) {
        if (j === i) seg.setAttribute('aria-current', 'true');
        else seg.removeAttribute('aria-current');
      });
      if (prev) prev.disabled = i === 0;
      if (next) next.disabled = i === last;
      if (current) current.textContent = (i < 9 ? '0' : '') + (i + 1);
      if (announce && status) {
        var name = cards[i].querySelector('.cspot__name');
        status.textContent = template
          .replace('[index]', i + 1)
          .replace('[count]', cards.length)
          .replace('[title]', name ? name.textContent.trim() : '');
      }
    }

    function go(i) {
      i = Math.max(0, Math.min(last, i));
      track.scrollTo({ left: offsetOf(i), behavior: Suki.prefersReducedMotion() ? 'auto' : 'smooth' });
      setActive(i, true);
    }

    track.addEventListener('scroll', function () {
      if (frame || drag) return;
      frame = requestAnimationFrame(function () {
        frame = 0;
        setActive(nearest(), true);
      });
    }, { passive: true });

    if (prev) prev.addEventListener('click', function () { go(index - 1); });
    if (next) next.addEventListener('click', function () { go(index + 1); });
    each(segs, function (seg) {
      seg.addEventListener('click', function () { go(Number(seg.getAttribute('data-cspot-go')) || 0); });
    });

    deck.addEventListener('keydown', function (event) {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      if (!track.contains(event.target)) return;
      event.preventDefault();
      go(index + (event.key === 'ArrowLeft' ? -1 : 1));
    });

    /* Mouse drag; touch and pen already scroll the row natively */
    function endDrag() {
      track.classList.remove('is-dragging');
    }

    track.addEventListener('pointerdown', function (event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      drag = { id: event.pointerId, x: event.clientX, left: track.scrollLeft, from: index, dx: 0, moved: false };
    });

    track.addEventListener('pointermove', function (event) {
      if (!drag || event.pointerId !== drag.id) return;
      drag.dx = event.clientX - drag.x;
      if (!drag.moved) {
        if (Math.abs(drag.dx) < 6) return;
        drag.moved = true;
        track.classList.add('is-dragging');
        try { track.setPointerCapture(drag.id); } catch (e) { /* capture is best effort */ }
      }
      track.scrollLeft = drag.left - drag.dx;
    });

    function onUp(event) {
      if (!drag || event.pointerId !== drag.id) return;
      var state = drag;
      drag = null;
      if (!state.moved) return;
      suppressClick = true;
      setTimeout(function () { suppressClick = false; }, 0);
      var target = nearest();
      if (Math.abs(state.dx) > 40 && target === state.from) target = state.from + (state.dx < 0 ? 1 : -1);
      go(target);
      /* Snap comes back once the smooth scroll has landed on a snap point */
      if ('onscrollend' in window) track.addEventListener('scrollend', endDrag, { once: true });
      else setTimeout(endDrag, 450);
    }

    track.addEventListener('pointerup', onUp);
    track.addEventListener('pointercancel', onUp);
    track.addEventListener('dragstart', function (event) { event.preventDefault(); });

    /* A drag must not also open the product; a half-scrolled card scrolls in */
    track.addEventListener('click', function (event) {
      if (suppressClick) {
        event.preventDefault();
        event.stopPropagation();
        suppressClick = false;
        return;
      }
      var card = event.target.closest('[data-cspot-card]');
      var i = cards.indexOf(card);
      if (i > -1 && i !== index) {
        event.preventDefault();
        event.stopPropagation();
        go(i);
      }
    }, true);

    setActive(nearest(), false);
  }

  /* ------------------------------------------------------------------------
     Module: predictiveSearch
     ------------------------------------------------------------------------ */

  var searchCount = 0;

  Suki.register('predictiveSearch', function init(scope) {
    if (!scope.querySelectorAll) return;
    each(scope.querySelectorAll('[data-search-input]'), bindSearch);
  });

  function bindSearch(input) {
    var container = input.closest('[data-search-dialog]') || input.closest('[data-predictive-search]') || input.form || doc;
    var results = container.querySelector('[data-predictive-results]');
    if (!results || !Suki.claim(input, 'predictive')) return;

    var route = Suki.config.routes.predictive_search || '/search/suggest';
    var controller = null;
    var lastQuery = '';
    var activeIndex = -1;

    searchCount += 1;
    if (!results.id) results.id = 'predictive-results-' + searchCount;
    if (!results.getAttribute('aria-label')) {
      results.setAttribute('aria-label', Suki.config.strings.predictive_label || 'Search suggestions');
    }
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-haspopup', 'listbox');
    input.setAttribute('aria-controls', results.id);
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('autocomplete', 'off');

    /* The section may mark its own options ([role="option"], as
       sections/predictive-search.liquid does); otherwise every link is one. */
    function options() {
      var marked = results.querySelectorAll('[role="option"]');
      return marked.length ? marked : results.querySelectorAll('a[href]');
    }

    function hrefOf(option) {
      if (!option) return '';
      if (option.matches('a[href]')) return option.href;
      var link = option.querySelector('a[href]');
      return link ? link.href : '';
    }

    function setActive(index) {
      var list = options();
      each(list, function (el) {
        el.classList.remove('is-active');
        el.setAttribute('aria-selected', 'false');
      });
      input.removeAttribute('aria-activedescendant');
      activeIndex = -1;
      if (!list.length || index < 0) return;
      var next = list[(index + list.length) % list.length];
      activeIndex = Array.prototype.indexOf.call(list, next);
      next.classList.add('is-active');
      next.setAttribute('aria-selected', 'true');
      input.setAttribute('aria-activedescendant', next.id);
      if (typeof next.scrollIntoView === 'function') next.scrollIntoView({ block: 'nearest' });
    }

    function annotate() {
      // One listbox: the section's own when it renders one, else the container.
      if (results.querySelector('[role="listbox"]')) {
        if (results.getAttribute('role') === 'listbox') results.removeAttribute('role');
      } else {
        results.setAttribute('role', 'listbox');
      }
      each(options(), function (el, i) {
        if (!el.id) el.id = results.id + '-option-' + i;
        if (!el.getAttribute('role')) el.setAttribute('role', 'option');
        el.setAttribute('aria-selected', 'false');
      });
    }

    function clear() {
      if (controller) controller.abort();
      controller = null;
      results.innerHTML = '';
      results.removeAttribute('aria-busy');
      results.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      setActive(-1);
      lastQuery = '';
    }

    function render(html, query) {
      var fresh = parseHtml(html);
      var section = fresh.querySelector('#shopify-section-predictive-search, .shopify-section') || fresh.body;
      results.innerHTML = section.innerHTML;
      results.hidden = false;
      results.removeAttribute('aria-busy');
      input.setAttribute('aria-expanded', 'true');
      annotate();
      setActive(-1);
      Suki.init(results, 'search:results');
      Suki.emit('suki:search:results', { query: query, results: results }, input);
    }

    function search(query) {
      if (controller) controller.abort();
      controller = 'AbortController' in window ? new AbortController() : null;
      results.setAttribute('aria-busy', 'true');

      var url = new URL(route, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product,page,article,collection');
      url.searchParams.set('resources[limit]', '4');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('section_id', 'predictive-search');

      fetch(url.toString(), {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: controller ? controller.signal : undefined
      }).then(function (response) {
        if (!response.ok) throw new Error(response.statusText);
        return response.text();
      }).then(function (html) {
        controller = null;
        if (input.value.trim() !== query) return;
        render(html, query);
      }).catch(function (err) {
        if (err && err.name === 'AbortError') return;
        results.removeAttribute('aria-busy');
        controller = null;
      });
    }

    var onInput = Suki.debounce(function () {
      var query = input.value.trim();
      if (query.length < 2) {
        clear();
        return;
      }
      if (query === lastQuery) return;
      lastQuery = query;
      search(query);
    }, 250);

    input.addEventListener('input', onInput);

    input.addEventListener('keydown', function (event) {
      var list = options();
      var open = input.getAttribute('aria-expanded') === 'true';
      switch (event.key) {
        case 'ArrowDown':
          if (!list.length) return;
          event.preventDefault();
          setActive(activeIndex + 1);
          break;
        case 'ArrowUp':
          if (!list.length) return;
          event.preventDefault();
          setActive(activeIndex <= 0 ? list.length - 1 : activeIndex - 1);
          break;
        case 'Home':
          if (!open || !list.length) return;
          event.preventDefault();
          setActive(0);
          break;
        case 'End':
          if (!open || !list.length) return;
          event.preventDefault();
          setActive(list.length - 1);
          break;
        case 'Enter':
          if (activeIndex >= 0 && hrefOf(list[activeIndex])) {
            event.preventDefault();
            window.location.assign(hrefOf(list[activeIndex]));
          }
          break;
        case 'Escape':
          if (open) {
            // First Escape clears the suggestions; the next one reaches the dialog.
            event.preventDefault();
            event.stopPropagation();
            clear();
          }
          break;
        default:
          break;
      }
    });

    // Re-run the search when the dialog reopens with a query still typed.
    var dialog = input.closest('dialog');
    if (dialog) {
      Suki.on('suki:dialog:open', function (event) {
        if (event.detail.dialog !== dialog) return;
        var query = input.value.trim();
        if (query.length >= 2 && !results.innerHTML.trim()) {
          lastQuery = query;
          search(query);
        }
      });
    }

    results.addEventListener('mousedown', function (event) {
      // Keep focus in the input so a click on a suggestion still navigates
      // (the input's blur handlers, if any, do not fire first).
      if (event.target.closest('a[href]')) event.preventDefault();
    });

    results.addEventListener('click', function (event) {
      var link = event.target.closest('a[href]');
      if (link) window.location.assign(link.href);
    });
  }

  /* Modules registered after theme.js booted run at once; before, they run
     on suki:ready with the rest. Nothing else to do here. */
})();
